import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import NotFound from './NotFound';
import { hasConsentChoice } from '../components/CookieConsent.jsx';
import { SOCIAL_PLATFORMS, detectPlatform, socialBioLinks, trackStoreClick } from '../utils/storeLinks';
import '../components/StoreCTA.css';
import './GoPage.css';

// GA4 runs event_callback as soon as gtag.js has queued the hit, not when it is
// sent. The page_view for /go/<platform> goes out right away, but for a first-time
// visitor (no cookie choice yet) gtag first holds every hit for consent's 500 ms
// wait_for_update, and leaving inside that window drops them, so we stay a little
// longer. The store_click itself is batched by GA4 and only goes out if the browser
// flushes it on unload (it does for the same-origin web redirect), so count bio
// clicks from the /go/* page_views and use store_click as a bonus. MAX_WAIT_MS caps
// the whole thing in case gtag.js is slow or blocked.
const CONSENT_WAIT_MS = 600;
const MAX_WAIT_MS = 2000;

const STORE_FOR_DEVICE = { ios: 'app_store', android: 'google_play', desktop: 'web' };

// Link-in-bio redirect: /go/<platform> (one URL per social profile).
// iPhone/iPad → Crowns on the App Store, Android → Crowns on Google Play,
// everything else → the web game. The prerendered HTML is a tiny page with
// plain links, so it still works if the redirect script never runs.
function GoRedirect({ platform }) {
  const links = socialBioLinks(platform);
  const page = `/go/${platform}`;
  const track = (store, placement, onSent) => trackStoreClick({
    app: 'crowns', store, placement, page, params: { social_platform: platform }, onSent,
  });

  useEffect(() => {
    if (window.__PRERENDER__) return undefined;
    const store = STORE_FOR_DEVICE[detectPlatform()];
    let left = false;
    const go = () => {
      if (left) return;
      left = true;
      // replace(): the back button returns to the social app, not to this page.
      window.location.replace(links[store]);
    };
    const timers = [setTimeout(go, MAX_WAIT_MS)];
    const extraWait = hasConsentChoice() ? 0 : CONSENT_WAIT_MS;
    track(store, 'social-bio', () => timers.push(setTimeout(go, extraWait)));
    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platform]);

  return (
    <div className="go-page">
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>Opening Crowns - Logic Puzzles CA</title>
      </Helmet>
      <div className="container go-inner">
        <img className="go-icon" src="/assets/logos/crowns_logo_256.webp" alt="Crowns app icon" width="88" height="88" />
        <h1>Opening Crowns…</h1>
        <p>If nothing happens, choose where to play:</p>
        <div className="store-cta-badges go-links">
          <a className="store-cta-badge" href={links.app_store} aria-label="Download Crowns on the App Store"
            onClick={() => track('app_store', 'social-bio-link')}>
            <img src="/app-store-badge.svg" alt="Download on the App Store" width="135" height="45" />
          </a>
          <a className="store-cta-badge" href={links.google_play} aria-label="Get Crowns on Google Play"
            onClick={() => track('google_play', 'social-bio-link')}>
            <img src="/play-store-badge.png" alt="Get it on Google Play" width="152" height="45" />
          </a>
        </div>
        <a className="button secondary go-web" href={links.web} onClick={() => track('web', 'social-bio-link')}>
          Play free on the web
        </a>
      </div>
    </div>
  );
}

function GoPage() {
  const platform = String(useParams().platform || '').toLowerCase();
  if (!SOCIAL_PLATFORMS.includes(platform)) return <NotFound />;
  return <GoRedirect platform={platform} />;
}

export default GoPage;
